import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { 
  ShieldCheck, 
  Truck, 
  Users, 
  MapPin, 
  Menu, 
  X,
  UserPlus,
  Search,
  MessageCircle,
  ChevronRight,
  ExternalLink,
  Building2,
  ShoppingBag,
  PackagePlus,
  Store,
  TrendingUp,
  ShoppingCart,
  BadgeCheck,
  Zap,
  Heart,
  VolumeX,
  Smartphone,
  Link2,
  LayoutDashboard,
  ClipboardList,
  RefreshCw,
  CheckCircle2,
  Loader2,
  Clock,
  Lock,
  Download,
  Sparkles
} from "lucide-react";
import { Button } from "../components/ui/button";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Logo URL
const LOGO_URL = "https://customer-assets.emergentagent.com/job_neighbor-market-6/artifacts/78ujw3e9_LocaLS_Logo.png";

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { duration: 0.6, ease: "easeOut" }
  }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.15 }
  }
};

// Header Component
const Header = ({ onOpenLead }) => {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <header 
      data-testid="header-nav" 
      className="fixed top-0 left-0 right-0 z-50 bg-brand-primary shadow-lg"
    >
      <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
        {/* Logo */}
        <a href="/" className="flex items-center gap-2" data-testid="logo-link">
          <div className="bg-white rounded-lg p-1">
            <img 
              src={LOGO_URL} 
              alt="LocaLS Logo" 
              className="h-10 w-auto object-contain"
            />
          </div>
        </a>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-8">
          <a 
            href="#how-it-works" 
            className="font-body text-text-inverse/90 hover:text-text-inverse transition-colors duration-200"
            data-testid="nav-how-it-works"
          >
            How it works
          </a>
          <a 
            href="#categories" 
            className="font-body text-text-inverse/90 hover:text-text-inverse transition-colors duration-200"
            data-testid="nav-categories"
          >
            Categories
          </a>
          <a 
            href="#community" 
            className="font-body text-text-inverse/90 hover:text-text-inverse transition-colors duration-200"
            data-testid="nav-community"
          >
            Community
          </a>
        </nav>

        {/* CTA Button */}
        <div className="hidden md:flex items-center gap-4">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/vendor/login')}
            className="font-body text-text-inverse/90 hover:text-text-inverse hover:bg-white/10"
            data-testid="sign-in-btn"
          >
            Sign In
          </Button>
          <Button 
            onClick={onOpenLead}
            className="font-body bg-white text-brand-primary hover:bg-white/90 rounded-full px-6"
            data-testid="join-btn"
          >
            Become a Vendor
          </Button>
        </div>

        {/* Mobile Menu Button */}
        <button 
          className="md:hidden p-2 text-text-inverse"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          data-testid="mobile-menu-btn"
        >
          {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <motion.div 
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          exit={{ opacity: 0, height: 0 }}
          className="md:hidden bg-brand-primary border-t border-white/20"
        >
          <nav className="flex flex-col p-6 gap-4">
            <a href="#how-it-works" className="font-body font-semibold text-text-inverse py-2">How it works</a>
            <a href="#categories" className="font-body font-semibold text-text-inverse py-2">Categories</a>
            <a href="#community" className="font-body font-semibold text-text-inverse py-2">Community</a>
            <hr className="border-white/20" />
            <Button
              variant="ghost"
              onClick={() => { setMobileMenuOpen(false); navigate('/vendor/login'); }}
              className="justify-start text-text-inverse"
            >
              Sign In
            </Button>
            <Button 
              onClick={() => { setMobileMenuOpen(false); onOpenLead && onOpenLead(); }}
              className="bg-white text-brand-primary hover:bg-white/90 rounded-full"
            >
              Become a Vendor
            </Button>
          </nav>
        </motion.div>
      )}
    </header>
  );
};

// Hero Section
const HeroSection = ({ onOpenLead }) => {
  return (
    <section 
      data-testid="hero-section" 
      className="pt-32 pb-20 sm:pt-40 sm:pb-32 px-6 sm:px-12"
    >
      <div className="max-w-7xl mx-auto">
        <div className="bento-hero">
          {/* Text Content */}
          <motion.div 
            className="flex flex-col justify-center"
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
          >
            <motion.span 
              variants={fadeInUp}
              className="font-body text-xs sm:text-sm font-bold uppercase tracking-[0.2em] text-brand-primary mb-4"
            >
              India's Hyperlocal Marketplace
            </motion.span>
            
            <motion.h1 
              variants={fadeInUp}
              className="font-heading text-5xl sm:text-6xl font-extrabold tracking-tight leading-none text-text-primary mb-6"
            >
              Your Society's
              <span className="text-brand-primary block mt-2">Marketplace</span>
            </motion.h1>
            
            <motion.p 
              variants={fadeInUp}
              className="font-body text-base sm:text-lg leading-relaxed text-text-secondary max-w-lg mb-8"
            >
              Discover amazing products, services, and deals right inside your own apartment society. 
              Buy, sell, and connect with your neighbors locally.
            </motion.p>
            
            <motion.div variants={fadeInUp} className="flex flex-wrap gap-4">
              <Button 
                onClick={() => document.getElementById('get-started')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                className="bg-brand-primary hover:bg-brand-primary-hover text-text-inverse rounded-full px-8 py-6 text-base font-medium brand-btn-shadow"
                data-testid="hero-explore-btn"
              >
                Explore Your Society
                <ChevronRight className="ml-2" size={18} />
              </Button>
              <Button 
                variant="outline"
                onClick={onOpenLead}
                className="rounded-full px-8 py-6 text-base font-medium border-surface-border text-text-primary hover:bg-background-secondary"
                data-testid="hero-vendor-btn"
              >
                Become a Vendor
              </Button>
            </motion.div>

            {/* Stats */}
            <motion.div 
              variants={fadeInUp}
              className="flex flex-wrap gap-8 mt-12 pt-8 border-t border-surface-border"
            >
              <div>
                <span className="font-heading font-bold text-3xl text-brand-primary">500+</span>
                <p className="font-body text-sm text-text-muted mt-1">Active Neighbors</p>
              </div>
              <div>
                <span className="font-heading font-bold text-3xl text-brand-secondary">1.2K</span>
                <p className="font-body text-sm text-text-muted mt-1">Items Listed</p>
              </div>
              <div>
                <span className="font-heading font-bold text-3xl text-brand-accent">98%</span>
                <p className="font-body text-sm text-text-muted mt-1">Happy Exchanges</p>
              </div>
            </motion.div>
          </motion.div>

          {/* Hero Images - Bento Layout */}
          <motion.div 
            className="grid grid-cols-2 gap-4 mt-8 md:mt-0"
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
          >
            <div className="col-span-2 rounded-[2rem] overflow-hidden img-hover-zoom">
              <img 
                src="https://images.pexels.com/photos/34450811/pexels-photo-34450811.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Modern apartment complex exterior"
                className="w-full h-64 sm:h-80 object-cover"
              />
            </div>
            <div className="rounded-[2rem] overflow-hidden img-hover-zoom bg-brand-secondary/20 p-6 flex flex-col justify-end">
              <Building2 className="text-brand-secondary mb-3" size={32} />
              <p className="font-heading font-semibold text-text-primary">Right Inside Your Society</p>
            </div>
            <div className="rounded-[2rem] overflow-hidden img-hover-zoom bg-brand-accent/20 p-6 flex flex-col justify-end">
              <MapPin className="text-brand-accent mb-3" size={32} />
              <p className="font-heading font-semibold text-text-primary">Zero Delivery Distance</p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Trust Banner
const TrustBanner = () => {
  const trustItems = [
    { icon: Building2, text: "Right Inside Your Society" },
    { icon: Truck, text: "Zero Shipping Fees" },
    { icon: Users, text: "Meet Your Neighbors" },
    { icon: MapPin, text: "Hyperlocal Trust" },
  ];

  return (
    <section 
      data-testid="trust-banner" 
      className="py-8 bg-background-secondary border-y border-surface-border overflow-hidden"
    >
      <div className="flex animate-marquee">
        {[...trustItems, ...trustItems, ...trustItems].map((item, index) => (
          <div 
            key={index} 
            className="flex items-center gap-3 px-12 whitespace-nowrap"
          >
            <item.icon className="text-brand-primary" size={20} />
            <span className="font-body font-medium text-text-secondary">{item.text}</span>
          </div>
        ))}
      </div>
    </section>
  );
};

// How It Works - TrackCard (hoisted to module level so Framer Motion state isn't reset on parent re-renders)
const TrackCard = ({ label, icon: LabelIcon, accent, steps, borderColor, cta }) => (
  <motion.div variants={fadeInUp} className="flex flex-col">
    {/* Heading OUTSIDE the card */}
    <div className="flex items-center gap-3 mb-5 sm:mb-6 px-1">
      <div className={`w-12 h-12 rounded-2xl ${accent} flex items-center justify-center shadow-sm`}>
        <LabelIcon className="text-brand-primary" size={24} />
      </div>
      <h3 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-text-primary leading-none">
        {label}
      </h3>
    </div>

    {/* Card content */}
    <div className={`bg-surface-card rounded-3xl p-6 sm:p-8 border ${borderColor} shadow-sm flex-1 flex flex-col`}>
      <div className="space-y-4 flex-1">
        {steps.map((step, index) => (
          <div key={index} className="relative">
            <div className="flex items-start gap-4 group">
              <div className="flex-shrink-0 flex flex-col items-center">
                <div className={`w-12 h-12 rounded-2xl ${accent} flex items-center justify-center shadow-sm group-hover:scale-105 transition-transform`}>
                  <step.icon className="text-brand-primary" size={22} />
                </div>
                {index < steps.length - 1 && (
                  <div className="w-px h-8 bg-border-subtle mt-2" />
                )}
              </div>
              <div className="flex-1 pb-2">
                <div className="flex items-baseline gap-2 mb-1">
                  <span className="font-heading text-xs font-bold text-brand-primary tracking-wider">
                    STEP {step.num}
                  </span>
                </div>
                <h4 className="font-heading text-lg sm:text-xl font-semibold text-text-primary mb-1">
                  {step.title}
                </h4>
                <p className="font-body text-sm sm:text-base leading-relaxed text-text-secondary">
                  {step.description}
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {cta && (
        <div className="pt-6 mt-4 border-t border-surface-border">
          <Button
            onClick={cta.onClick}
            className={`w-full rounded-full px-6 py-5 text-base font-semibold brand-btn-shadow ${
              cta.variant === "outline"
                ? "bg-surface-card border border-brand-primary text-brand-primary hover:bg-brand-primary hover:text-text-inverse"
                : "bg-brand-primary hover:bg-brand-primary-hover text-text-inverse"
            }`}
            data-testid={cta.testId}
          >
            {cta.icon && <cta.icon className="mr-2" size={18} />}
            {cta.label}
          </Button>
        </div>
      )}
    </div>
  </motion.div>
);

// How It Works Section
const HowItWorksSection = ({ onOpenLead }) => {
  const customerSteps = [
    {
      num: "01",
      title: "Sign Up",
      description: "Create your account in minutes with your mobile number and basic details.",
      icon: UserPlus,
    },
    {
      num: "02",
      title: "Select Your Apartment",
      description: "Pick your society from the list and unlock the marketplace for your community.",
      icon: Building2,
    },
    {
      num: "03",
      title: "Start Shopping",
      description: "Browse trusted neighbors, order homemade goodies and services—zero shipping fees.",
      icon: ShoppingBag,
    }
  ];

  const vendorSteps = [
    {
      num: "01",
      title: "Sign Up",
      description: "Register as a vendor from your apartment and start your free trial.",
      icon: UserPlus,
    },
    {
      num: "02",
      title: "List Your Items",
      description: "Add products or services with photos, prices and descriptions in a few taps.",
      icon: PackagePlus,
    },
    {
      num: "03",
      title: "Start Selling",
      description: "Reach neighbors in your society, receive orders and grow your home business.",
      icon: TrendingUp,
    }
  ];

  return (
    <section 
      id="how-it-works"
      data-testid="how-it-works" 
      className="py-20 sm:py-32 px-6 sm:px-12"
    >
      <div className="max-w-7xl mx-auto">
        <motion.div 
          className="text-center mb-12 sm:mb-16"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={fadeInUp}
        >
          <span className="font-body text-xs sm:text-sm font-bold uppercase tracking-[0.2em] text-brand-primary mb-4 block">
            Getting Started
          </span>
          <h2 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight leading-none text-text-primary">
            How LocaLS Works
          </h2>
          <p className="font-body text-base sm:text-lg text-text-secondary mt-4 max-w-2xl mx-auto">
            Whether you want to shop from trusted neighbors or sell what you make—here's how to get started.
          </p>
        </motion.div>

        <motion.div 
          className="grid md:grid-cols-2 gap-6 sm:gap-8"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={staggerContainer}
        >
          <TrackCard 
            label="For Customers"
            icon={ShoppingCart}
            accent="bg-brand-primary/10"
            borderColor="border-brand-primary/20"
            steps={customerSteps}
            cta={{
              label: "Download App",
              icon: Download,
              variant: "outline",
              testId: "track-customer-cta",
              onClick: () => {
                const el = document.getElementById("get-started");
                if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
              }
            }}
          />
          <TrackCard 
            label="For Vendors"
            icon={Store}
            accent="bg-brand-secondary/20"
            borderColor="border-brand-secondary/30"
            steps={vendorSteps}
            cta={{
              label: "Become a Vendor",
              icon: Store,
              variant: "solid",
              testId: "track-vendor-cta",
              onClick: onOpenLead,
            }}
          />
        </motion.div>
      </div>
    </section>
  );
};

// Categories Preview Section
const CategoriesSection = () => {
  const categories = [
    {
      name: "Food and Beverages",
      image: "https://images.unsplash.com/photo-1758910536889-43ce7b3199fd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHwxfHxJbmRpYW4lMjBob21lbWFkZSUyMGZvb2QlMjBzd2VldHMlMjBtaXRoYWl8ZW58MHx8fHwxNzc1ODAwOTQ3fDA&ixlib=rb-4.1.0&q=85",
      className: "category-1"
    },
    {
      name: "Plants and Gardening",
      image: "https://images.unsplash.com/photo-1560889234-d51c44848e01?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODl8MHwxfHNlYXJjaHwxfHxwb3R0ZWQlMjBwbGFudHMlMjBpbmRvb3IlMjBob21lJTIwZGVjb3J8ZW58MHx8fHwxNzc1ODAwOTU0fDA&ixlib=rb-4.1.0&q=85",
      className: "category-2"
    },
    {
      name: "Arts and Crafts",
      image: "https://images.pexels.com/photos/34400038/pexels-photo-34400038.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
      className: "category-3"
    },
    {
      name: "Tutoring and Services",
      image: "https://images.pexels.com/photos/7079148/pexels-photo-7079148.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
      className: "category-4"
    },
    {
      name: "Many more",
      image: "https://images.pexels.com/photos/5632398/pexels-photo-5632398.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
      className: "category-5",
      isMore: true
    }
  ];

  return (
    <section 
      id="categories"
      data-testid="category-preview" 
      className="py-20 sm:py-32 px-6 sm:px-12 bg-background-secondary"
    >
      <div className="max-w-7xl mx-auto">
        <motion.div 
          className="text-center mb-16"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={fadeInUp}
        >
          <span className="font-body text-xs sm:text-sm font-bold uppercase tracking-[0.2em] text-brand-primary mb-4 block">
            What You Can Find
          </span>
          <h2 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight leading-none text-text-primary">
            Explore Categories
          </h2>
        </motion.div>

        <motion.div 
          className="bento-categories"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={staggerContainer}
        >
          {categories.map((category, index) => (
            <motion.div
              key={index}
              variants={fadeInUp}
              className={`${category.className} rounded-3xl overflow-hidden relative group cursor-pointer img-hover-zoom`}
            >
              <img 
                src={category.image} 
                alt={category.name}
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className={`absolute inset-0 ${category.isMore ? 'bg-gradient-to-t from-brand-primary/85 via-brand-primary/40 to-transparent' : 'bg-gradient-to-t from-text-primary/80 via-text-primary/20 to-transparent'}`} />
              <div className="absolute bottom-0 left-0 right-0 p-6">
                {category.isMore && (
                  <Sparkles className="text-text-inverse mb-2" size={22} />
                )}
                <h3 className="font-heading text-xl sm:text-2xl font-semibold text-text-inverse">
                  {category.name}
                </h3>
                {category.isMore && (
                  <p className="font-body text-sm text-text-inverse/80 mt-1">
                    & lots more from your neighbors
                  </p>
                )}
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

// Community Story Section
const CommunitySection = () => {
  return (
    <section 
      id="community"
      data-testid="community-story" 
      className="py-20 sm:py-32 px-6 sm:px-12"
    >
      <div className="max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Image */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6 }}
            className="order-2 lg:order-1"
          >
            <div className="relative">
              <div className="rounded-[2rem] overflow-hidden img-hover-zoom">
                <img 
                  src="https://images.pexels.com/photos/3935342/pexels-photo-3935342.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                  alt="Modern apartment building community"
                  className="w-full h-80 sm:h-[500px] object-cover"
                />
              </div>
              {/* Floating card */}
              <div className="absolute -bottom-6 -right-6 sm:bottom-8 sm:right-[-2rem] bg-surface-card rounded-2xl p-5 shadow-xl border border-surface-border">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-brand-secondary/30 flex items-center justify-center">
                    <Users className="text-brand-secondary" size={24} />
                  </div>
                  <div>
                    <p className="font-heading font-bold text-2xl text-text-primary">12K+</p>
                    <p className="font-body text-sm text-text-muted">Happy Neighbors</p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Text Content */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
            className="order-1 lg:order-2"
          >
            <motion.span 
              variants={fadeInUp}
              className="font-body text-xs sm:text-sm font-bold uppercase tracking-[0.2em] text-brand-primary mb-4 block"
            >
              Our Story
            </motion.span>
            
            <motion.h2 
              variants={fadeInUp}
              className="font-heading text-4xl sm:text-5xl font-bold tracking-tight leading-none text-text-primary mb-6"
            >
              More Than Just
              <span className="text-brand-primary block mt-2">Buying & Selling</span>
            </motion.h2>
            
            <motion.p 
              variants={fadeInUp}
              className="font-body text-base sm:text-lg leading-relaxed text-text-secondary mb-6"
            >
              It's about building a stronger, safer, and more connected neighborhood. 
              When you buy from a neighbor, you're not just getting a great deal—you're 
              supporting someone in your own society.
            </motion.p>
            
            <motion.p 
              variants={fadeInUp}
              className="font-body text-base sm:text-lg leading-relaxed text-text-secondary mb-8"
            >
              LocaLS was born from a simple idea: the best marketplace is one where 
              you already know and trust the people around you. No anonymous sellers, 
              no long shipping waits, no strangers—just neighbors helping neighbors.
            </motion.p>

            <motion.div variants={fadeInUp} className="flex flex-wrap gap-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-brand-primary/10 flex items-center justify-center">
                  <ShieldCheck className="text-brand-primary" size={20} />
                </div>
                <span className="font-body font-medium text-text-primary">Safe & Secure</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-brand-secondary/20 flex items-center justify-center">
                  <Users className="text-brand-secondary" size={20} />
                </div>
                <span className="font-body font-medium text-text-primary">Community First</span>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Lead Modal - collects early-access details
const LeadModal = ({ open, onClose }) => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", apartment_name: "" });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  if (!open) return null;

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.name.trim() || !form.email.trim() || !form.phone.trim() || !form.apartment_name.trim()) {
      setError("Please fill in all fields.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/leads`, form);
      setSuccess(true);
      setTimeout(() => {
        setSuccess(false);
        setForm({ name: "", email: "", phone: "", apartment_name: "" });
        onClose();
      }, 1800);
    } catch (err) {
      const msg = err?.response?.data?.detail;
      setError(typeof msg === "string" ? msg : "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="lead-modal"
      className="fixed inset-0 z-[9998] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-text-primary/60 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Dialog */}
      <motion.div
        initial={{ opacity: 0, y: 20, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.25 }}
        className="relative bg-surface-card rounded-3xl shadow-2xl w-full max-w-md p-6 sm:p-8 border border-surface-border"
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute top-4 right-4 p-2 rounded-full hover:bg-background-secondary text-text-secondary transition-colors"
          data-testid="lead-modal-close"
        >
          <X size={20} />
        </button>

        {success ? (
          <div className="text-center py-6">
            <div className="w-16 h-16 rounded-full bg-brand-primary/10 flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="text-brand-primary" size={36} />
            </div>
            <h3 className="font-heading text-2xl font-bold text-text-primary mb-2">
              You're on the list!
            </h3>
            <p className="font-body text-text-secondary">
              We'll be in touch shortly with your personal order link.
            </p>
          </div>
        ) : (
          <>
            <div className="mb-5">
              <span className="font-body text-xs font-bold uppercase tracking-[0.18em] text-brand-primary block mb-2">
                Early Access
              </span>
              <h3 className="font-heading text-2xl sm:text-3xl font-bold text-text-primary leading-tight">
                Become a Vendor
              </h3>
              <p className="font-body text-sm text-text-secondary mt-2">
                Share a few details and we'll reach out to set you up with your personal order link.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-3" data-testid="lead-form">
              <div>
                <label className="font-body text-sm font-medium text-text-primary block mb-1">
                  Full Name
                </label>
                <input
                  name="name"
                  type="text"
                  value={form.name}
                  onChange={handleChange}
                  placeholder="e.g. Priya Sharma"
                  className="w-full rounded-xl border border-surface-border bg-background-primary px-4 py-3 font-body text-text-primary placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-brand-primary/40 focus:border-brand-primary transition"
                  data-testid="lead-name"
                />
              </div>
              <div>
                <label className="font-body text-sm font-medium text-text-primary block mb-1">
                  Email
                </label>
                <input
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={handleChange}
                  placeholder="you@example.com"
                  className="w-full rounded-xl border border-surface-border bg-background-primary px-4 py-3 font-body text-text-primary placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-brand-primary/40 focus:border-brand-primary transition"
                  data-testid="lead-email"
                />
              </div>
              <div>
                <label className="font-body text-sm font-medium text-text-primary block mb-1">
                  Phone
                </label>
                <input
                  name="phone"
                  type="tel"
                  value={form.phone}
                  onChange={handleChange}
                  placeholder="+91 98765 43210"
                  className="w-full rounded-xl border border-surface-border bg-background-primary px-4 py-3 font-body text-text-primary placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-brand-primary/40 focus:border-brand-primary transition"
                  data-testid="lead-phone"
                />
              </div>
              <div>
                <label className="font-body text-sm font-medium text-text-primary block mb-1">
                  Apartment Name
                </label>
                <input
                  name="apartment_name"
                  type="text"
                  value={form.apartment_name}
                  onChange={handleChange}
                  placeholder="e.g. Prestige Lakeside Habitat"
                  className="w-full rounded-xl border border-surface-border bg-background-primary px-4 py-3 font-body text-text-primary placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-brand-primary/40 focus:border-brand-primary transition"
                  data-testid="lead-apartment"
                />
                <p className="font-body text-xs text-text-muted mt-1.5 leading-relaxed">
                  Just type your apartment name—our backend team will get in touch to set up a
                  proper account within your apartment soon.
                </p>
              </div>

              {error && (
                <p className="font-body text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">
                  {error}
                </p>
              )}

              <Button
                type="submit"
                disabled={submitting}
                className="w-full bg-brand-primary hover:bg-brand-primary-hover text-text-inverse rounded-full px-8 py-6 text-base font-semibold brand-btn-shadow mt-2 disabled:opacity-70"
                data-testid="lead-submit"
              >
                {submitting ? (
                  <>
                    <Loader2 className="mr-2 animate-spin" size={18} />
                    Submitting...
                  </>
                ) : (
                  <>
                    Become a Vendor
                    <ChevronRight className="ml-2" size={18} />
                  </>
                )}
              </Button>
            </form>
          </>
        )}
      </motion.div>
    </div>
  );
};

// One Link, All Your Orders Section
const OneLinkSection = ({ onOpenLead }) => {
  const features = [
    {
      title: "A single shareable order link",
      description: "One link you can share in WhatsApp groups, bios, or DMs for all your customers.",
      icon: Link2,
    },
    {
      title: "A central dashboard",
      description: "View and manage every order from one clean, organized place—no more scattered chats.",
      icon: LayoutDashboard,
    },
    {
      title: "Clear order tracking",
      description: "Never miss a request. Every order is logged, timestamped, and easy to find.",
      icon: ClipboardList,
    },
    {
      title: "Simple status updates",
      description: "Mark orders as received, preparing, ready, or delivered—customers stay informed.",
      icon: RefreshCw,
    },
    {
      title: "Built for apartment vendors",
      description: "Designed specifically for home chefs, service providers, and society-based sellers.",
      icon: Building2,
    },
    {
      title: "Works 24/7 — never miss an order",
      description: "Your order link stays open around the clock, so customers can place orders anytime, even when you're busy.",
      icon: Clock,
    },
  ];

  return (
    <section
      id="one-link"
      data-testid="one-link"
      className="py-20 sm:py-32 px-6 sm:px-12"
    >
      <div className="max-w-7xl mx-auto">
        {/* Intro */}
        <motion.div
          className="text-center max-w-3xl mx-auto mb-12 sm:mb-16"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={staggerContainer}
        >
          <motion.span
            variants={fadeInUp}
            className="font-body text-xs sm:text-sm font-bold uppercase tracking-[0.2em] text-brand-primary mb-4 block"
          >
            For Vendors
          </motion.span>
          <motion.h2
            variants={fadeInUp}
            className="font-heading text-4xl sm:text-5xl md:text-6xl font-bold tracking-tight leading-[1.05] text-text-primary"
          >
            One Link. 
            <span className="text-brand-primary"> All Your Orders.</span>
          </motion.h2>
          <motion.p
            variants={fadeInUp}
            className="font-body text-base sm:text-lg leading-relaxed text-text-secondary mt-6"
          >
            Stop juggling orders across WhatsApp, calls, and messages. With LocaLS, you simply share
            your personal order link with residents, and every order comes into one clean,
            easy-to-manage dashboard.
          </motion.p>
          <motion.p
            variants={fadeInUp}
            className="font-body text-base sm:text-lg leading-relaxed text-text-secondary mt-4"
          >
            Track orders, manage requests, and stay organized without missing a single customer.
            Whether you're a home chef, service provider, or selling everyday items—handle
            everything in one place.
          </motion.p>
        </motion.div>

        {/* What You Get */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={staggerContainer}
          className="mb-12 sm:mb-16"
        >
          <motion.h3
            variants={fadeInUp}
            className="font-heading text-2xl sm:text-3xl font-bold text-text-primary mb-8 text-center"
          >
            What You Get
          </motion.h3>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
            {features.map((feat, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                data-testid={`onelink-feature-${index}`}
                className="bg-surface-card rounded-3xl p-6 border border-surface-border shadow-sm card-hover"
              >
                <div className="w-12 h-12 rounded-2xl bg-brand-primary/10 flex items-center justify-center mb-4 shadow-sm">
                  <feat.icon className="text-brand-primary" size={24} />
                </div>
                <h4 className="font-heading text-lg sm:text-xl font-semibold text-text-primary mb-2 leading-tight">
                  {feat.title}
                </h4>
                <p className="font-body text-sm sm:text-base leading-relaxed text-text-secondary">
                  {feat.description}
                </p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Closing line + CTA */}
        <motion.div
          className="bg-gradient-to-br from-brand-primary/10 via-brand-secondary/10 to-brand-accent/10 rounded-3xl p-8 sm:p-12 text-center border border-brand-primary/10"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={staggerContainer}
        >
          <motion.h3
            variants={fadeInUp}
            className="font-heading text-2xl sm:text-3xl md:text-4xl font-bold text-text-primary leading-tight max-w-3xl mx-auto"
          >
            Turn scattered conversations into 
            <span className="text-brand-primary"> structured orders</span> with LocaLS.
          </motion.h3>
          <motion.div variants={fadeInUp} className="mt-8">
            <Button
              onClick={onOpenLead}
              className="bg-brand-primary hover:bg-brand-primary-hover text-text-inverse rounded-full px-10 py-6 text-base sm:text-lg font-semibold brand-btn-shadow"
              data-testid="onelink-cta-btn"
            >
              Become a Vendor
              <ChevronRight className="ml-2" size={20} />
            </Button>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

// Why LocaLS Section (Key Differentiators)
const WhyLocaLSSection = () => {
  const differentiators = [
    {
      title: "Right Inside Your Society",
      description: "Sellers are part of your own apartment community. Place an order, walk down for pickup, or get it dropped to your door — no middlemen, no detours.",
      icon: Building2,
      accent: "bg-brand-primary/10",
      iconColor: "text-brand-primary",
    },
    {
      title: "Hyperlocal Convenience",
      description: "Your marketplace is literally next door. Discover what your own society has to offer, instantly.",
      icon: MapPin,
      accent: "bg-brand-secondary/20",
      iconColor: "text-brand-secondary",
    },
    {
      title: "Faster Delivery / Pickup",
      description: "Skip long shipping waits—collect in the lobby or get it at your door in minutes, not days.",
      icon: Zap,
      accent: "bg-brand-accent/20",
      iconColor: "text-brand-primary",
    },
    {
      title: "Support Local Sellers",
      description: "Every purchase empowers a neighbor's home business and keeps value within your community.",
      icon: Heart,
      accent: "bg-brand-primary/10",
      iconColor: "text-brand-primary",
    },
    {
      title: "No Noise from Outside Vendors",
      description: "Only residents can list here—no spam, no outside sellers, just clean, trusted listings.",
      icon: VolumeX,
      accent: "bg-brand-secondary/20",
      iconColor: "text-brand-secondary",
    },
    {
      title: "Private to Your Society",
      description: "Listings are visible only to residents of your apartment—your community marketplace stays truly private.",
      icon: Lock,
      accent: "bg-brand-accent/20",
      iconColor: "text-brand-primary",
    },
  ];

  return (
    <section
      id="why-locals"
      data-testid="why-locals"
      className="py-20 sm:py-32 px-6 sm:px-12 bg-background-secondary"
    >
      <div className="max-w-7xl mx-auto">
        <motion.div
          className="text-center mb-12 sm:mb-16"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={fadeInUp}
        >
          <span className="font-body text-xs sm:text-sm font-bold uppercase tracking-[0.2em] text-brand-primary mb-4 block">
            Why Choose Us
          </span>
          <h2 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight leading-none text-text-primary">
            Why LocaLS
          </h2>
          <p className="font-body text-base sm:text-lg text-text-secondary mt-4 max-w-2xl mx-auto">
            Built on trust, proximity, and genuine community—here's what makes LocaLS different.
          </p>
        </motion.div>

        <motion.div
          className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={staggerContainer}
        >
          {differentiators.map((item, index) => (
            <motion.div
              key={index}
              variants={fadeInUp}
              data-testid={`why-item-${index}`}
              className="bg-surface-card rounded-3xl p-6 sm:p-8 border border-surface-border shadow-sm card-hover"
            >
              <div className={`w-14 h-14 rounded-2xl ${item.accent} flex items-center justify-center mb-5 shadow-sm`}>
                <item.icon className={item.iconColor} size={28} />
              </div>
              <h3 className="font-heading text-xl sm:text-2xl font-semibold text-text-primary mb-3 leading-tight">
                {item.title}
              </h3>
              <p className="font-body text-base leading-relaxed text-text-secondary">
                {item.description}
              </p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

// CTA Section
const CTASection = ({ onOpenLead }) => {
  // Apple logo SVG
  const AppleIcon = () => (
    <svg viewBox="0 0 384 512" className="w-6 h-6 fill-current" aria-hidden="true">
      <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z"/>
    </svg>
  );

  // Google Play logo SVG
  const GooglePlayIcon = () => (
    <svg viewBox="0 0 512 512" className="w-6 h-6" aria-hidden="true">
      <path fill="#FFC107" d="m325.3 234.3-91.9-52.6-69.3 69.3 77.6 77.7 83.6-47.8c11.2-6.4 11.2-23.9 0-30.3l.1-16.3z"/>
      <path fill="#FF3D00" d="M164.1 120.7 54.6 35.1C46.9 29 36 32.1 31.9 41.1c-1.6 3.6-2 7.7-.9 11.6l.1.4 109.9 193.6 87.6-85.4-64.5-40.6z"/>
      <path fill="#4CAF50" d="M140.9 246.7 31 440.8c-.5 1.4-.8 2.9-.8 4.4 0 8.2 6.6 14.9 14.9 14.9 2.9 0 5.7-.8 8.1-2.4l110.9-86.5-23.2-124.5z"/>
      <path fill="#1976D2" d="m241.7 250.7-77.6-77.7-87.6 85.4 64.5 40.6 100.7-48.3z"/>
    </svg>
  );

  return (
    <section 
      id="get-started"
      data-testid="bottom-cta" 
      className="py-20 sm:py-32 px-6 sm:px-12 relative overflow-hidden"
    >
      {/* Background Image with Overlay */}
      <div 
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1714119486465-9a8fd405106b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjV8MHwxfHNlYXJjaHwxfHx3YXJtJTIwcGFzdGVsJTIwYWJzdHJhY3QlMjBiYWNrZ3JvdW5kfGVufDB8fHx8MTc3NTgwMDIyNXww&ixlib=rb-4.1.0&q=85')`
        }}
      />
      <div className="absolute inset-0 bg-white/85 backdrop-blur-md" />

      <div className="max-w-4xl mx-auto relative z-10">
        <motion.div 
          className="text-center"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={staggerContainer}
        >
          <motion.span 
            variants={fadeInUp}
            className="font-body text-xs sm:text-sm font-bold uppercase tracking-[0.2em] text-brand-primary mb-4 block"
          >
            Join the Community
          </motion.span>

          <motion.h2 
            variants={fadeInUp}
            className="font-heading text-4xl sm:text-5xl font-bold tracking-tight leading-tight text-text-primary mb-6"
          >
            Get Started with 
            <span className="text-brand-primary"> LocaLS</span> Today
          </motion.h2>
          
          <motion.p 
            variants={fadeInUp}
            className="font-body text-base sm:text-lg leading-relaxed text-text-secondary max-w-2xl mx-auto mb-10"
          >
            Join thousands of residents across India who are already discovering amazing deals,
            making new connections, and strengthening their society. Your neighborhood
            marketplace awaits.
          </motion.p>
          
          <motion.div variants={fadeInUp} className="flex flex-wrap justify-center gap-4 mb-10">
            <Button 
              onClick={onOpenLead}
              className="bg-brand-primary hover:bg-brand-primary-hover text-text-inverse rounded-full px-10 py-6 text-lg font-medium brand-btn-shadow"
              data-testid="cta-join-btn"
            >
              Become a Vendor
              <ChevronRight className="ml-2" size={20} />
            </Button>
          </motion.div>

          {/* App Store / Play Store buttons */}
          <motion.div variants={fadeInUp}>
            <p className="font-body text-sm text-text-muted mb-4 uppercase tracking-wider font-semibold">
              Coming soon on mobile
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a
                href="#"
                aria-disabled="true"
                onClick={(e) => e.preventDefault()}
                data-testid="app-store-btn"
                className="group inline-flex items-center gap-3 bg-text-primary hover:bg-text-primary/90 text-text-inverse rounded-2xl px-5 py-3 transition-transform hover:-translate-y-0.5 shadow-md"
              >
                <AppleIcon />
                <div className="flex flex-col items-start leading-none">
                  <span className="font-body text-[10px] uppercase tracking-wider opacity-80">
                    Download on the
                  </span>
                  <span className="font-heading text-lg font-semibold">
                    App Store
                  </span>
                </div>
              </a>

              <a
                href="#"
                aria-disabled="true"
                onClick={(e) => e.preventDefault()}
                data-testid="play-store-btn"
                className="group inline-flex items-center gap-3 bg-text-primary hover:bg-text-primary/90 text-text-inverse rounded-2xl px-5 py-3 transition-transform hover:-translate-y-0.5 shadow-md"
              >
                <GooglePlayIcon />
                <div className="flex flex-col items-start leading-none">
                  <span className="font-body text-[10px] uppercase tracking-wider opacity-80">
                    Get it on
                  </span>
                  <span className="font-heading text-lg font-semibold">
                    Google Play
                  </span>
                </div>
              </a>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  return (
    <footer 
      data-testid="footer" 
      className="py-12 px-6 sm:px-12 bg-brand-primary text-text-inverse"
    >
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row justify-between items-center gap-8">
          {/* Logo */}
          <div className="flex items-center gap-2">
            <div className="bg-white rounded-lg p-1">
              <img 
                src={LOGO_URL} 
                alt="LocaLS Logo" 
                className="h-8 w-auto object-contain"
              />
            </div>
          </div>

          {/* Links */}
          <nav className="flex flex-wrap justify-center gap-6">
            <Link 
              to="/privacy-policy" 
              className="font-body font-semibold text-text-inverse hover:text-text-inverse/80 transition-colors"
              data-testid="footer-privacy"
            >
              Privacy Policy
            </Link>
            <a 
              href="#" 
              className="font-body font-semibold text-text-inverse hover:text-text-inverse/80 transition-colors"
              data-testid="footer-terms"
            >
              Terms of Service
            </a>
            <a 
              href="#" 
              className="font-body font-semibold text-text-inverse hover:text-text-inverse/80 transition-colors"
              data-testid="footer-contact"
            >
              Contact Us
            </a>
          </nav>

          {/* Built by LitSpark */}
          <a 
            href="https://litspark.solutions/" 
            target="_blank" 
            rel="noopener noreferrer"
            className="font-body font-semibold text-text-inverse hover:text-text-inverse/80 transition-colors flex items-center gap-1"
            data-testid="footer-litspark"
          >
            Built by LitSpark
            <ExternalLink size={14} />
          </a>
        </div>

        <div className="mt-8 pt-8 border-t border-white/20 text-center">
          <p className="font-body font-medium text-text-inverse/90">
            © 2026 LocaLS. All rights reserved. | Made with love for Indian communities.
          </p>
        </div>
      </div>
    </footer>
  );
};

// Main Landing Page Component
const LandingPage = () => {
  const [leadModalOpen, setLeadModalOpen] = useState(false);
  const openLead = () => setLeadModalOpen(true);

  return (
    <div className="min-h-screen bg-background-primary">
      <Header onOpenLead={openLead} />
      <main>
        <HeroSection onOpenLead={openLead} />
        <TrustBanner />
        <HowItWorksSection onOpenLead={openLead} />
        <CategoriesSection />
        <OneLinkSection onOpenLead={openLead} />
        <WhyLocaLSSection />
        <CommunitySection />
        <CTASection onOpenLead={openLead} />
      </main>
      <Footer />
      <LeadModal open={leadModalOpen} onClose={() => setLeadModalOpen(false)} />
    </div>
  );
};

export default LandingPage;
